import { isRecord } from '@/shared/lib';
import { type ConversationSelection } from '../model/selection';
import { isConversationSelection } from './conversationSelection';

/** Имя query-параметра, которым адрес списка диалогов ссылается на отложенную выборку. */
export const CONVERSATION_SELECTION_QUERY_PARAM = 'selection';

/** Префикс ключей отложенных выборок в хранилище браузера. */
const STASH_KEY_PREFIX = 'conversationSelection:';

/**
 * Срок жизни отложенной выборки. Забравшая её вкладка снимает выборку сама; запись
 * остаётся только если вкладка не открылась вовсе, и такие записи чистятся по возрасту.
 */
const STASH_TTL_MS = 10 * 60 * 1000;

/** Отложенная выборка вместе с моментом сохранения — по нему чистятся невостребованные записи. */
type StashedSelection = {
  selection: ConversationSelection;
  savedAt: number;
};

const isStashedSelection = (value: unknown): value is StashedSelection => {
  if (!isRecord(value)) return false;
  const { selection, savedAt } = value as Partial<StashedSelection>;
  return typeof savedAt === 'number' && isConversationSelection(selection);
};

/** Удаляет записи, которые никто не забрал: их вкладка так и не открылась. */
const dropExpiredSelections = (): void => {
  const now = Date.now();
  const staleKeys = Object.keys(localStorage).filter((key) => {
    if (!key.startsWith(STASH_KEY_PREFIX)) return false;
    const raw = localStorage.getItem(key);
    if (!raw) return true;
    try {
      const parsed: unknown = JSON.parse(raw);
      return !isStashedSelection(parsed) || now - parsed.savedAt > STASH_TTL_MS;
    } catch {
      return true;
    }
  });
  staleKeys.forEach((key) => localStorage.removeItem(key));
};

/**
 * Откладывает выборку для новой вкладки и возвращает ключ к ней.
 *
 * Состояние навигации принадлежит истории вкладки и в новую не попадает, а уложить состав
 * в адрес нельзя — это сотни, а у критерия и тысячи идентификаторов. Поэтому в адрес идёт
 * только ключ, а сама выборка ждёт в хранилище браузера.
 */
export const stashConversationSelection = (selection: ConversationSelection): string => {
  dropExpiredSelections();
  const key = crypto.randomUUID();
  const stashed: StashedSelection = { selection, savedAt: Date.now() };
  localStorage.setItem(`${STASH_KEY_PREFIX}${key}`, JSON.stringify(stashed));
  return key;
};

/**
 * Забирает отложенную выборку по ключу и снимает её с хранилища: иначе повторный заход
 * по тому же адресу открывал бы устаревший состав. `null` — по ключу выборки нет.
 */
export const takeConversationSelection = (key: string): ConversationSelection | null => {
  const storageKey = `${STASH_KEY_PREFIX}${key}`;
  const raw = localStorage.getItem(storageKey);
  if (!raw) return null;
  localStorage.removeItem(storageKey);
  try {
    const parsed: unknown = JSON.parse(raw);
    return isStashedSelection(parsed) ? parsed.selection : null;
  } catch {
    return null;
  }
};
